import React, { useEffect, useState, useRef } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { theme } from 'antd';
import { FilterDropdownProps } from 'antd/es/table/interface';
import CustomSelect from 'components/CustomSelect';
import NestedTableFilterDropdown from 'components/NestedTableFilterDropdown';
import TextBoxesContainer from 'components/TextBoxesContainer';
import Helpers from 'utils/Helpers';
import TextAreaBoxGap from 'components/TextAreaBoxGap';
import { RootState } from 'store/configureStore';
import { gapsOp } from 'store/ducks/Gaps';
import {
  IAuditData,
  IGaps,
  IGapsData,
  IGapsTableData
} from 'store/ducks/Gaps/types';
import { complianceEstOp } from 'store/ducks/ComplienceEst';
import CustomTable from '../CustomTable';
import NestedTable from '../NestedTable';
import { gapNoticedOptions } from 'constants/filterOptions';
import { getDynamicCategories } from 'constants/categorieNames';
import i18n from 'constants/translation/i18n';
import { generateUniqueId } from 'utils/hooks/generateUniqueId';
import useClickOutside from 'utils/hooks/useClickOutside';
import { useSearchableColumn } from 'utils/useSearchableColumn';
import * as Styled from './GapsDescriptionTable.styled';

const { useToken } = theme;

const MemoizedChildComponent = React.memo(TextAreaBoxGap);

const GapsDescriptionTable: React.FC = () => {
  const { token } = useToken();
  const dispatch = useDispatch();
  const { gapsSaveData, gapsUpdated } = useSelector(
    (state: RootState) => state.gaps
  );

  const {
    lang,
    loading,
    saveClicked,
    drawTable,
    gapsDescription,
    tableFilters,
    gapsTableData,
    scheme,
    orgContext
  } = useSelector((state: RootState) => ({
    lang: state.global.lang,
    drawTable: state.complianceEst.drawTable,
    loading: state.gaps.loading,
    gapsDescription: state.gaps.gapsDescription,
    tableFilters: state.gaps.tableFilters,
    gapsTableData: state.gaps.gapsTableData,
    saveClicked: state.complianceEst.saveClicked,
    scheme: state.global.scheme,
    orgContext: state.global.hierarchicalLevelsScope
  }));

  const [filteredData, setFilteredData] = useState<IGapsTableData[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [showCategoryFilter, setShowCategoryFilter] = useState(false);
  const [categoryFilter, setCategoryFilter] = useState<string>('all');
  const categoryFilterRef = useRef<HTMLDivElement>(null);
  const [, forceUpdate] = useState({});
  const filterParams = Helpers.toApiFilters(orgContext);

  // Use simple searchable column
  const {
    searchText,
    filteredData: searchFilteredData,
    column: searchColumn
  } = useSearchableColumn(gapsDescription || [], 'value', i18n.t('CLN_REQ'));

  useEffect(() => {
    i18n.changeLanguage(lang);
    forceUpdate({});
  }, [lang]);

  const handleChildChange = (updatedData: IGaps) => {
    const updatedState = () => {
      const newState = { ...gapsSaveData };

      Object.keys(updatedData).forEach((key: number | string) => {
        if (newState.hasOwnProperty(key)) {
          newState[key] = {
            ...newState[key],
            ...updatedData[key]
          };
        } else {
          newState[key] = updatedData[key];
        }
      });

      return newState;
    };

    dispatch(gapsOp.setGapsSaveData(updatedState()));
  };

  const handleChildDelete = (key: number | string) => {
    const updatedState = { ...gapsSaveData };
    delete updatedState[key];
    dispatch(gapsOp.setGapsSaveData(updatedState));
  };

  useEffect(() => {
    if (!Helpers.isObjEmpty(gapsSaveData) && saveClicked) {
      const userInfo = Helpers.getUserInfo()?.user;

      const gapsWithOwner = Object.values(gapsSaveData).map((gap: any) => ({
        ...gap,

        gap_id: gap.gap_id && gap.gap_id > 0 ? gap.gap_id : generateUniqueId(),

        gap_owner_ref: userInfo?.email || userInfo?.id
      }));

      gapsOp
        .saveGaps(gapsWithOwner, lang)
        .then(() => {
          dispatch(complianceEstOp.setSaveClicked(false));
          dispatch(gapsOp.setGapsSaveData({}));
        })
        .catch(() => {
          dispatch(complianceEstOp.setSaveClicked(false));
        });
    }
  }, [saveClicked, gapsSaveData, lang, dispatch]);

  useEffect(() => {
    const isEmpty = Helpers.isObjEmpty(gapsSaveData);
    // Update save button state immediately
    dispatch(complianceEstOp.setSaveDisabled(isEmpty));
  }, [gapsSaveData, dispatch]);

  useClickOutside(
    categoryFilterRef,
    showCategoryFilter,
    () => setShowCategoryFilter(false),
    'category-filter-dropdown'
  );

  const renderCategoryDropdown = () => {
    if (!showCategoryFilter) return null;
    return (
      <Styled.CategoryDropdownPortal className='category-filter-dropdown'>
        {[{ value: 'all', label: 'All Categories' }, ...categoryOptions].map(
          (option) => (
            <Styled.DropdownOption
              key={option.value}
              selected={categoryFilter === option.label}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onMouseUp={(e) => {
                e.preventDefault();
                e.stopPropagation();
                setCategoryFilter(
                  option.value === 'all' ? 'all' : option.label
                );
                setShowCategoryFilter(false);
              }}
            >
              {option.label}
            </Styled.DropdownOption>
          )
        )}
      </Styled.CategoryDropdownPortal>
    );
  };

  const gapDefinitionColumns = [
    searchColumn,
    {
      title: () => (
        <Styled.CategoryHeaderContainer
          ref={categoryFilterRef}
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            setShowCategoryFilter(!showCategoryFilter);
          }}
        >
          <span>{i18n.t('CATEGORY')}</span>
          <Styled.CategoryFilterIcon scheme={scheme} />
          {renderCategoryDropdown()}
        </Styled.CategoryHeaderContainer>
      ),
      width: 200,
      render: () => <Styled.PlaceholderDiv width={200} height={20} />
    },
    // {
    //   title: i18n.t('DROPPED_EVIDENCES'),
    //   width: 450,
    //   render: () => <Styled.PlaceholderDiv width={450} height={20} />
    // },
    {
      title: i18n.t('DROPPED_AUDIT_FINDINGS'),
      width: 400,
      render: () => <Styled.PlaceholderDiv width={400} height={20} />
    },
    {
      title: () => (
        <Styled.MergedColumnsContainer>
          <Styled.MergedColumnSm token={token}>
            {i18n.t('GAP_NOTICED')}
          </Styled.MergedColumnSm>
          <Styled.MergedColumnMd token={token}>
            {i18n.t('GAPS')}
          </Styled.MergedColumnMd>
        </Styled.MergedColumnsContainer>
      ),
      width: 501,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm
      }: FilterDropdownProps) => (
        <NestedTableFilterDropdown
          setSelectedKeys={setSelectedKeys}
          selectedKeys={selectedKeys}
          confirm={confirm}
          options={gapNoticedOptions}
          defaultValue='all'
          filterName='gapNoticed'
        />
      ),
      render: () => <Styled.PlaceholderDiv width={501} height={20} />
    }
  ];

  const expandedRow = (record: IGapsTableData) => {
    if (!record || typeof record !== 'object') {
      return null;
    }
    const dataArr: IGapsData[] = Array.isArray(record[0]) ? record[0] : [];

    const nestedTableColumns = [
      {
        title: () => (
          <Styled.ColumnHeaderContainer>
            <span>Regulatory Requirements</span>
            <Styled.SearchIcon
              scheme={scheme}
              onClick={() => {
                /* Add search functionality for nested table if needed */
              }}
            />
          </Styled.ColumnHeaderContainer>
        ),
        dataIndex: [],
        key: 'value',
        width: 500,
        render: (dataObj: IGapsData) => (
          <Styled.ClientReqTextArea
            value={dataObj.value}
            rows={3}
            level={dataObj?.level}
            width={500}
            readOnly={false}
            scheme={scheme}
          />
        )
      },
      {
        title: 'Category',
        dataIndex: 'category',
        key: 'category',
        width: 200,
        render: (category: string) => (
          <Styled.CategoryContent>
            <CustomSelect
              options={categoryOptions}
              defaultValue={category}
              disabled
            />
          </Styled.CategoryContent>
        )
      },
      // {
      //   title: 'Dropped evidences',
      //   dataIndex: 'EVIDENCES',
      //   key: 'EVIDENCES',
      //   width: 450,
      //   render: (evidences: IEvidence) => (
      //     <TextBoxesContainer data={evidences} descName='evd_desc' />
      //   )
      // },
      {
        title: 'Audit Findings',
        dataIndex: 'AUDITS',
        key: 'AUDITS',
        width: 400,
        render: (audits: IAuditData) => (
          <Styled.DroppedAuditFindingsContent>
            <TextBoxesContainer data={audits} descName='desc' />
          </Styled.DroppedAuditFindingsContent>
        )
      },
      {
        title: () => (
          <Styled.MergedColumnsContainer>
            <Styled.MergedColumnSm token={token}>
              Gap Noticed
            </Styled.MergedColumnSm>
            <Styled.MergedColumnMd token={token}>Gaps</Styled.MergedColumnMd>
          </Styled.MergedColumnsContainer>
        ),
        dataIndex: [],
        key: 'GAPS',
        width: 501,
        render: (data: IGapsData) => (
          <Styled.GapsNoticedContent>
            <MemoizedChildComponent
              placeholder='Gap description'
              data={data.GAPS}
              refId={data?.id}
              withCheckbox
              onChange={handleChildChange}
              onDelete={handleChildDelete}
            />
          </Styled.GapsNoticedContent>
        )
      }
    ];

    return (
      <NestedTable
        key={dataArr[0]?.id || 'nested-table'}
        className='nested-table'
        columns={nestedTableColumns}
        dataSource={Array.isArray(dataArr) ? dataArr : []}
        pagination={false}
        token={token}
      />
    );
  };

  useEffect(() => {
    try {
      if (!gapsDescription || !Array.isArray(gapsDescription)) {
        setFilteredData([]);
        return;
      }

      let dataToFilter = gapsDescription;

      // Apply search filter if search text exists
      if (searchText.trim()) {
        dataToFilter = searchFilteredData;
      }

      if (dataToFilter.length > 0) {
        // Handle the new simplified data structure
        if (
          Array.isArray(dataToFilter) &&
          dataToFilter[0] &&
          'id' in dataToFilter[0]
        ) {
          const categoryFiltered =
            categoryFilter === 'all'
              ? (dataToFilter as unknown as IGapsData[])
              : (dataToFilter as unknown as IGapsData[]).filter(
                  (item: IGapsData) => item.category === categoryFilter
                );
          const rows = categoryFiltered.map(
            (item: IGapsData, index: number) => ({
              0: [item],
              key: `gaps-row-${index}-${JSON.stringify(item).slice(0, 10)}`
            })
          ) as IGapsTableData[];
          setFilteredData(rows);
        } else {
          setFilteredData([]);
        }
      } else {
        setFilteredData([]);
      }
    } catch (error) {
      setFilteredData([]);
    }
  }, [gapsDescription, tableFilters, searchText, categoryFilter]);

  useEffect(() => {
    try {
      if (!filteredData || !Array.isArray(filteredData)) {
        dispatch(gapsOp.setGapsTableData([]));
        return;
      }

      if (filteredData.length > 0) {
        const validData = filteredData
          .filter((item: IGapsTableData) => {
            return (
              item &&
              typeof item === 'object' &&
              item[0] &&
              Array.isArray(item[0]) &&
              item[0].length > 0
            );
          })
          .map((item: IGapsTableData, index: number) => ({
            ...item,
            key: `gaps-row-${index}-${JSON.stringify(item).slice(0, 10)}`
          }));
        dispatch(gapsOp.setGapsTableData(validData));
      } else {
        dispatch(gapsOp.setGapsTableData([]));
      }
    } catch (error) {
      dispatch(gapsOp.setGapsTableData([]));
    }
  }, [filteredData, dispatch]);

  // Load categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        const categories = await getDynamicCategories(lang);
        setCategoryOptions(categories);
      } catch (error) {
        console.error('Failed to load categories:', error);
        setCategoryOptions([]);
      }
    };
    loadCategories();
  }, []);

  // Fetch gaps description data when drawTable becomes true
  useEffect(() => {
    const hasFilters = Object.keys(filterParams).length > 0;
    if (!orgContext || Object.keys(orgContext).length === 0) return;

    if (!hasFilters) return;
    if (drawTable) {
      gapsOp
        .fetchGapsDescription('en' as any, filterParams)
        .then((response) => {
          try {
            if (Array.isArray(response) && response.length > 0) {
              // Data is now directly usable without additional wrapping
              dispatch(gapsOp.setGapsDescription(response));
            } else {
              dispatch(gapsOp.setGapsDescription([]));
            }
          } catch (error) {
            dispatch(gapsOp.setGapsDescription([]));
          }
        })
        .catch((error) => {
          dispatch(gapsOp.setGapsDescription([]));
        });
    }
  }, [dispatch, drawTable, orgContext]);

  const safeGapsTableData = Array.isArray(gapsTableData) ? gapsTableData : [];
  const hasValidData =
    Array.isArray(gapsDescription) &&
    gapsDescription.length > 0 &&
    gapsDescription[0] &&
    typeof gapsDescription[0] === 'object';

  // Add loading guard to prevent rendering with invalid data
  if (loading) {
    return (
      <CustomTable
        drawTable={false}
        columns={gapDefinitionColumns}
        itemsCount={0}
        dataSource={[]}
        token={token}
        scheme={scheme}
      />
    );
  }

  return (
    <>
      <style>{Styled.HiddenMainRowStyles}</style>
      {hasValidData && !gapsUpdated ? (
        <CustomTable
          drawTable
          token={token}
          scheme={scheme}
          columns={gapDefinitionColumns}
          dataSource={safeGapsTableData}
          itemsCount={safeGapsTableData.length}
          // dataSource={filteredData?.[0]?.[0] ?? []}
          loading={loading}
          onRow={(record: IGapsTableData, index?: number) => {
            if (
              record &&
              typeof record === 'object' &&
              record[0] &&
              Array.isArray(record[0])
            ) {
              return {
                className: 'hidden-placeholder-row'
              };
            }
            return {};
          }}
          expandable={{
            expandedRowKeys: safeGapsTableData.map(
              (item: IGapsTableData, index: number) =>
                `gaps-row-${index}-${JSON.stringify(item).slice(0, 10)}`
            ),
            expandedRowRender: (record: IGapsTableData) => expandedRow(record)
          }}
        />
      ) : (
        <CustomTable
          drawTable={drawTable}
          columns={gapDefinitionColumns}
          itemsCount={0}
          dataSource={[]}
          token={token}
          scheme={scheme}
        />
      )}
    </>
  );
};

export default GapsDescriptionTable;
